import { useState } from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import resizeWorker from 'store/resizeWorker';
import { errorMessage } from './errorMessage';
import { SlotBudget, fitsSlotBudget, slotBudgetHint } from 'utils/slotBudget';

interface ResizeWorkerDialogProps {
  workerId: string;
  workerName: string;
  currentSlots: number;
  heldSlots?: number;
  replacing?: string[];
  // The node's slot budget, when it has one: growth past it is checked before submit.
  slotBudget?: SlotBudget | null;
  // Shown only for a worker that runs FAST tasks above its slots (pytailor 0.40+).
  currentOvershoot?: number | null;
  overshootSupported?: boolean;
  onClose: () => void;
  onConfirm: (slots: number) => void;
}

export default function ResizeWorkerDialog({
  workerId,
  workerName,
  currentSlots,
  heldSlots,
  replacing,
  slotBudget,
  currentOvershoot,
  overshootSupported = false,
  onClose,
  onConfirm,
}: ResizeWorkerDialogProps) {
  const isReplacing = (replacing?.length ?? 0) > 0;
  const [slots, setSlots] = useState(String(currentSlots));
  const [overshoot, setOvershoot] = useState(String(currentOvershoot ?? 0));
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const normalized = slots.trim();
  const valid = /^\d+$/.test(normalized);
  const parsed = valid ? Number(normalized) : Number.NaN;
  const fits = !valid || fitsSlotBudget(slotBudget ?? null, parsed, currentSlots);
  const budgetHint = valid ? slotBudgetHint(slotBudget ?? null, parsed, currentSlots) : null;
  // Digits alone are not enough: a long-enough run of them is Infinity, which JSON.stringify
  // sends as null — and a null overshoot means "leave it unchanged", so the resize would look
  // like it took while the old overshoot stood.
  const overshootNumber = Number(overshoot.trim());
  const overshootValid =
    !overshootSupported || (/^\d+$/.test(overshoot.trim()) && Number.isSafeInteger(overshootNumber));
  const parsedOvershoot = overshootValid && overshootSupported ? overshootNumber : undefined;

  const handleConfirm = async () => {
    if (isSubmitting || !valid || !overshootValid) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await resizeWorker(workerId, parsed, parsedOvershoot);
      onConfirm(parsed);
      onClose();
    } catch (err) {
      console.error('Failed to resize worker:', err);
      setError(`Failed to resize worker: ${await errorMessage(err)}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setError(null);
    onClose();
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Resize Worker</DialogTitle>
      <DialogContent>
        <DialogContentText gutterBottom>
          {isReplacing ? (
            <>
              Set the slot budget for worker <strong>{workerName}</strong>. It is taking over from{' '}
              {replacing!.join(', ')} and holds {heldSlots ?? 0} of its {currentSlots} slots so far; the rest move here
              as the workers it replaces finish their running tasks. The budget is the size the group ends up at.
            </>
          ) : (
            <>
              Set the number of slots for worker <strong>{workerName}</strong>.
            </>
          )}
          <br />
          Scaling down drains surplus slots: running tasks finish first, then the slots are released (no work is lost).
          Setting <strong>0</strong> parks the worker as a warm standby — it keeps running but takes no new tasks until
          you scale it back up.
        </DialogContentText>
        <TextField
          label="Slots"
          type="number"
          value={slots}
          onChange={(e) => setSlots(e.target.value)}
          fullWidth
          margin="normal"
          slotProps={{ htmlInput: { min: 0 } }}
          error={!valid || !fits}
          helperText={
            !valid
              ? 'Enter a whole number of slots (0 or more).'
              : `${isReplacing ? `Currently ${currentSlots}; the worker holds ${heldSlots ?? 0} of them so far.` : `Currently ${currentSlots}.`}${budgetHint ? ` ${budgetHint}` : ''}`
          }
        />
        {overshootSupported && (
          <TextField
            label="Overshoot (FAST)"
            type="number"
            value={overshoot}
            onChange={(e) => setOvershoot(e.target.value)}
            fullWidth
            margin="normal"
            slotProps={{ htmlInput: { min: 0 } }}
            error={!overshootValid}
            helperText={
              overshootValid
                ? `FAST-lane tasks this worker runs above its slots; other lanes never use them. Currently ${currentOvershoot ?? 0}. Not charged to the node slot budget.`
                : 'Enter a whole number (0 or more).'
            }
          />
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={isSubmitting || !valid || !fits || !overshootValid}
        >
          {isSubmitting ? 'Resizing…' : 'Resize'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
